import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { Validators } from "@angular/forms";
import { BehaviorSubject } from "rxjs";
import {filter, map, tap} from "rxjs/operators";

import { AdvancedForm } from "../../../model/advanced-form.model";
import { Logger } from "../../../model/logger.model";
import { Campaign } from "../../../persistence/dto/campaign.model";
import { CommercialStructure } from "../../../persistence/dto/commercial-structure.model";
import { CampaignService } from "../../../service/campaign.service";
import { CommercialStructureService } from "../../../service/commercial-structure.service";
import { LoggingService } from "../../../service/logging.service";
import { Optional } from "../../../support/optional.model";
import { Unsubscriber } from "../../../support/unsubscriber.support";
import {forEach} from "lodash";

	/**
	 * @description
	 *	Vector de estructura que permite construir tuplas de la forma
	 *	(CampaignId, DivisionLevelId, ZoneLevelId, AreaLevelId), con acceso
	 *	restringido según el tipo de usuario actualmente logueado (principal).
	 */

@Component({
	selector: "x-structure-vector",
	styleUrls: ["./structure-vector.component.scss"],
	templateUrl: "./structure-vector.component.html"
})
export class StructureVectorComponent implements AfterViewInit, OnDestroy, OnInit {

	private logger: Logger = this.loggingService.getLogger("structure-vector.component");
	private unsubscriber = new Unsubscriber();

	@Input("advanced.form")
	public advancedForm: AdvancedForm;

	@Input("filterInvoicing.form")
	public filterInvoicing: Boolean;

    @Input("filterZones")
    public filterZones: Boolean = false;

	@Output("is.processing")
	public isProcessing = new EventEmitter<boolean>();

	public campaigns = new BehaviorSubject<Campaign[]>([]);
	public commercialStructure = new BehaviorSubject<Optional<CommercialStructure>>(Optional.empty());
	public countries = new BehaviorSubject<Optional<CommercialStructure>>(Optional.empty());
	public commerces = new BehaviorSubject<Optional<CommercialStructure>>(Optional.empty());
	public divisions = new BehaviorSubject<Optional<CommercialStructure>>(Optional.empty());
	public zones = new BehaviorSubject<Optional<CommercialStructure>>(Optional.empty());
	public areas = new BehaviorSubject<Optional<CommercialStructure>>(Optional.empty());

	public constructor(
		private campaignService: CampaignService,
		private commercialStructureService: CommercialStructureService,
		private loggingService: LoggingService) {
	}

	public ngOnInit(): void {
		this.isProcessing.emit(true);
		Optional.of(this.advancedForm)
			.orElseThrow(() => new Error(
				"Debe especificar un AdvancedForm para utilizar el elemento 'x-structure-vector'."));
		if(this.filterInvoicing){
			this.unsubscriber = new Unsubscriber()
			.addSubscription(this.campaignService.getInvoincingCampaign()
				.subscribe({
					next: campaigns => {
						this.campaigns.next(campaigns.sort((c1, c2) => c2.sequence - c1.sequence));
						this.isProcessing.emit(false);
					},
					error: (error: Error) => {
						this.logger.error(error);
						this.isProcessing.emit(false);
					}
				}));
		}
		else {
			this.unsubscriber = new Unsubscriber()
			.addSubscription(this.campaignService.allCampaigns()
				.subscribe({
					next: campaigns => {
						this.campaigns.next(campaigns.sort((c1, c2) => c2.sequence - c1.sequence));
						this.isProcessing.emit(false);
					},
					error: (error: Error) => {
						this.logger.error(error);
						this.isProcessing.emit(false);
					}
				}));
		}
	}

    public campaignChangedTrigger(campaignId: number): void {
        const oldQuery = this.advancedForm.load({});
					this.advancedForm.patch({ countryLevelId: undefined });
					this.commercialStructure.next(Optional.empty());
					this.loadLevels(campaignId, oldQuery);
    }

	public ngAfterViewInit(): void {
		this.unsubscriber
			.addSubscription(this.advancedForm.netChangeIfPresent("campaignId")
				.subscribe((campaignId: number) => {
                    this.campaignChangedTrigger(campaignId);
				}))
			.addSubscription(this.commercialStructure.asObservable()
				.pipe(
                    tap(commercialStructure => {
                        if (commercialStructure.isPresent() && commercialStructure.value.subStructure.length > 1) {
                            forEach(commercialStructure.value.subStructure[0].subStructure[0].subStructure, (subStructure) => {
                                let invoicedZone = commercialStructure.value.subStructure[1].subStructure[0].subStructure.find(zone => zone.id === subStructure.id);
                                if (invoicedZone){
                                    subStructure.subStructure = subStructure.subStructure.concat(invoicedZone.subStructure);
                                }
                            });

                            forEach(commercialStructure.value.subStructure[1].subStructure[0].subStructure, (subStructure) => {
                                let divisionAdded = commercialStructure.value.subStructure[0].subStructure[0].subStructure.find(division => division.id === subStructure.id);
                                if (!divisionAdded){
                                    commercialStructure.value.subStructure[0].subStructure[0].subStructure.push(subStructure);
                                }
                            });
                            commercialStructure.value.subStructure[0].subStructure.concat(commercialStructure.value.subStructure[1].subStructure);
                            let newCommercialStructure = commercialStructure;
                            newCommercialStructure.value.subStructure = commercialStructure.value.subStructure.slice(0, -1);
                            return newCommercialStructure;
                        }
                    }),
					filter(optionalCommercialStructure => optionalCommercialStructure.isPresent()),
					map(optionalCommercialStructure => optionalCommercialStructure.value))
				.subscribe((commercialStructure) => {
					this.advancedForm.patch({ countryLevelId: undefined });
					this.logger.debug(commercialStructure);
					this.countries.next(Optional.of(commercialStructure));
				}))
			.addSubscription(this.advancedForm.netChangeOver("countryLevelId")
				.pipe(map(netChange => netChange.delta["countryLevelId"]))
				.subscribe(countryLevelId => {
					const commerces = this.countries.value
						.flatMap(commercialStructure => commercialStructure
							.relativeQueryById(countryLevelId));
					this.commerces.next(commerces);
					this.advancedForm.patch({ commerceLevelId: undefined });
				}))
			.addSubscription(this.advancedForm.netChangeOver("commerceLevelId")
				.pipe(map(netChange => netChange.delta["commerceLevelId"]))
				.subscribe(commerceLevelId => {
					const divisions = this.commerces.value
						.flatMap(commercialStructure => commercialStructure
							.relativeQueryById(commerceLevelId));
					this.divisions.next(divisions);
 					this.advancedForm.patch({ divisionLevelId: undefined });
				}))
			.addSubscription(this.advancedForm.netChangeOver("divisionLevelId")
				.pipe(map(netChange => netChange.delta["divisionLevelId"]))
				.subscribe(divisionLevelId => {
					let zones = this.divisions.value
						.flatMap(commercialStructure => commercialStructure
							.relativeQueryByIdAndInvoiced(divisionLevelId));
                    if (this.filterZones && zones.value) {
                        zones.value.subStructure =  zones.value.subStructure.filter(zone => zone.isInvoiced === 1);
                    }
					this.zones.next(zones);
					this.advancedForm.patch({ zoneLevelId: undefined });
				}))
			.addSubscription(this.advancedForm.netChangeOver("zoneLevelId")
				.pipe(map(netChange => netChange.delta["zoneLevelId"]))
				.subscribe(zoneLevelId => {
					const areas = this.zones.value
						.flatMap(commercialStructure => commercialStructure
							.relativeQueryById(zoneLevelId));
					this.areas.next(areas);
					this.advancedForm.patch({ areaLevelId: undefined });
				}));
	}

	public ngOnDestroy(): void {
		this.isProcessing.complete();
		this.campaigns.complete();
		this.commercialStructure.complete();
		this.countries.complete();
		this.commerces.complete();
		this.divisions.complete();
		this.zones.complete();
		this.areas.complete();
		this.unsubscriber.releaseSubscriptions();
	}

	public areaToString(zoneWithArea: number): string {
		return `${zoneWithArea}`.substring(3);
	}

	public getOrDefault(commercialStructure: Optional<CommercialStructure>): CommercialStructure[] {
		return commercialStructure
			.map(commercialStructure => commercialStructure.subStructure)
			.getOrElse([]);
	}

	private loadLevels(campaignId: number, oldQuery: any): void {
		this.isProcessing.emit(true);
		this.commercialStructureService.byCampaignId(campaignId, this.filterInvoicing)
			.subscribe({
				next: commercialStructure => {
					this.logger.debug(`All campaign levels loaded for campaign ${campaignId}.`);
					this.commercialStructure.next(Optional.of(commercialStructure));
					// Es importante que la reasignación se realice sobre la nueva estructura:
					if (!this.tryToReassignStructure(this.commercialStructure.value, oldQuery)) {
						// Si no se pudo reasignar la consulta, cargar prefijo:
						this.loadPrefix(this.commercialStructure.value);
					}
                    this.isProcessing.emit(false);
				},
				error: (error: Error) => {
					this.logger.error(error);
					this.isProcessing.emit(false);
				}
			});
	}

	private loadPrefix(commercialStructure: Optional<CommercialStructure>): void {
		commercialStructure.ifPresent(commercialStructure => {
			const prefix = commercialStructure.prefix();
			this.logger.debug(`The prefix is: ${prefix}.`);

			if (prefix[2] != null) {
				this.advancedForm.control("divisionLevelId").setValidators(Validators.required);
			}
			if (prefix[3] != null) {
				this.advancedForm.control("zoneLevelId").setValidators(Validators.required);
			}
			if (prefix[4] != null) {
				this.advancedForm.control("areaLevelId").setValidators(Validators.required);
			}

			this.advancedForm.patchInOrder([
				{ countryLevelId: prefix[0] },
				{ commerceLevelId: prefix[1] },
				{ divisionLevelId: prefix[2] },
				{ zoneLevelId: prefix[3] },
				{ areaLevelId: prefix[4] }
			]);

			if (this.zones.value.value != undefined && this.zones.value.value.subStructure.length == 0) {
				this.advancedForm.patch({ zoneLevelId: undefined });
			}
		});
	}

	private tryToReassignStructure(commercialStructure: Optional<CommercialStructure>, oldQuery: any): boolean {
		this.logger.debug("The old query was:", oldQuery);
		return commercialStructure
			.flatMap(commercialStructure => commercialStructure
				.ascendingQueryById(
					oldQuery.countryLevelId,
					oldQuery.commerceLevelId,
					oldQuery.divisionLevelId,
					oldQuery.zoneLevelId,
					CommercialStructure.areaWithZoneOrUndefined(oldQuery.zoneLevelId, oldQuery.areaLevelId)
				)
				.map(structure => structure.ascendingPrefix())
				.map(prefix => {
					this.logger.debug("Reassignation with:", prefix);

					if (prefix[2] != null) {
						this.advancedForm.control("divisionLevelId").setValidators(Validators.required);
					}
					if (prefix[3] != null) {
						this.advancedForm.control("zoneLevelId").setValidators(Validators.required);
					}
					if (prefix[4] != null) {
						this.advancedForm.control("areaLevelId").setValidators(Validators.required);
					}

					this.advancedForm.patchInOrder([
						{ countryLevelId: prefix[0] },
						{ commerceLevelId: prefix[1] },
						{ divisionLevelId: prefix[2] },
						{ zoneLevelId: prefix[3] },
						{ areaLevelId: prefix[4] }
					]);

					if (this.zones.value.value.subStructure.length == 0) {
						this.advancedForm.patch({ zoneLevelId: undefined });
					}
					return true;
				}))
		.getOrElse(false);
	}
}
